/**
 * Guards California / peer comparisons so unlike metrics are not narrated as comparable.
 */
export type VehicleScope = 'ldv' | 'hdv' | 'all' | 'unknown';
export type MetricBasis = 'daily' | 'annual' | 'total' | 'rate' | 'unknown';

export interface MetricComparatorFacts {
  metricId: string;
  vehicleScope: VehicleScope;
  unit: string;
  geographyLevel: string;
  year: number | null;
  denominator?: string | null;
  modes?: string[];
  provenance?: string;
  sourceDataset?: string | null;
  sourceVintage?: string | null;
  basis?: MetricBasis;
  aggregationMethod?: string | null;
}

function modeSetKey(modes?: string[]): string {
  if (!modes?.length) return '';
  return [...modes].sort().join('|');
}

export function canCompareMetrics(
  local: MetricComparatorFacts,
  comparator: MetricComparatorFacts
): { compatible: boolean; reasons: string[] } {
  const reasons: string[] = [];
  if (local.metricId !== comparator.metricId) {
    reasons.push(`metricId mismatch (${local.metricId} vs ${comparator.metricId})`);
  }
  if (local.vehicleScope !== comparator.vehicleScope) {
    reasons.push(`vehicle scope mismatch (${local.vehicleScope} vs ${comparator.vehicleScope})`);
  }
  if (local.unit !== comparator.unit) {
    reasons.push(`unit mismatch (${local.unit} vs ${comparator.unit})`);
  }
  if (local.year != null && comparator.year != null && local.year !== comparator.year) {
    reasons.push(`year mismatch (${local.year} vs ${comparator.year})`);
  }
  if (local.denominator && comparator.denominator && local.denominator !== comparator.denominator) {
    reasons.push(`denominator mismatch (${local.denominator} vs ${comparator.denominator})`);
  }
  if (local.basis && comparator.basis && local.basis !== comparator.basis) {
    reasons.push(`basis mismatch (${local.basis} vs ${comparator.basis})`);
  }
  if (
    local.sourceDataset &&
    comparator.sourceDataset &&
    local.sourceDataset !== comparator.sourceDataset
  ) {
    reasons.push(`source dataset mismatch (${local.sourceDataset} vs ${comparator.sourceDataset})`);
  }
  if (
    local.sourceVintage &&
    comparator.sourceVintage &&
    local.sourceVintage !== comparator.sourceVintage
  ) {
    reasons.push(`source vintage mismatch (${local.sourceVintage} vs ${comparator.sourceVintage})`);
  }
  const localModes = modeSetKey(local.modes);
  const compModes = modeSetKey(comparator.modes);
  if (localModes && compModes && localModes !== compModes) {
    reasons.push(`mode set mismatch (${localModes} vs ${compModes})`);
  }
  if (
    (local.denominator && !comparator.denominator) ||
    (!local.denominator && comparator.denominator)
  ) {
    reasons.push('denominator missing on one side');
  }
  const ldvAllMismatch =
    (local.vehicleScope === 'ldv' && comparator.vehicleScope === 'all') ||
    (local.vehicleScope === 'all' && comparator.vehicleScope === 'ldv');
  if (ldvAllMismatch) {
    reasons.push('LDV vs all-vehicle scope is not comparable');
  }
  return { compatible: reasons.length === 0, reasons };
}

export const VMT_LDV_PER_CAPITA_FACTS = (year: number): MetricComparatorFacts => ({
  metricId: 'vmt_ldv_per_capita_daily',
  vehicleScope: 'ldv',
  unit: 'miles/resident/day',
  geographyLevel: 'place',
  year,
  denominator: 'population_same_year',
  modes: ['PRIVATE_AUTO', 'ON_DEMAND_AUTO'],
  provenance: 'replica_vmt_by_mode + backcast',
  sourceDataset: 'replica_vmt_by_mode',
  sourceVintage: String(year),
  basis: 'daily',
  aggregationMethod: 'ldv_modes_sum',
});

export const VMT_ALL_VEHICLE_PER_CAPITA_FACTS = (year: number): MetricComparatorFacts => ({
  metricId: 'vmt_all_per_capita_daily',
  vehicleScope: 'all',
  unit: 'miles/resident/day',
  geographyLevel: 'state',
  year,
  denominator: 'population_same_year',
  provenance: 'all_modes',
  sourceDataset: 'replica_vmt_by_mode',
  basis: 'daily',
  aggregationMethod: 'all_emfac_modes',
});
